import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

/**
 * New Transport UI's day/date selector. Two render modes:
 *  - default (compact horizontal strip) — used by `EmbeddedTransportView`'s
 *    dashboard widget.
 *  - `floating` — a larger, frozen rounded pill fixed along the bottom of
 *    the Transport page (both Map View and View All, View + Edit mode),
 *    always visible regardless of scroll/layout. Same date-select / prev /
 *    next / drag-to-change-date contract either way.
 */
const CompactDateBar = ({ dates, selectedDateStr, onSelectDate, onPrev, onNext, isDateLocked, dragOverDate, onDragOverDate, onDragLeaveDate, onDropOnDate, floating = false, large = false }) => {
  if (floating) {
    return (
      <div
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-2.5 rounded-full shadow-2xl border border-white/15 pointer-events-auto"
        style={{ background: 'rgba(15,23,42,0.92)', backdropFilter: 'blur(20px)' }}
        data-testid="transport-new-compact-date-bar"
      >
        <button
          data-testid="transport-new-compact-prev-day-btn"
          onClick={onPrev}
          title="Previous week"
          className="w-9 h-9 rounded-full hover:bg-white/15 flex items-center justify-center text-white flex-shrink-0"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-1.5 overflow-x-auto max-w-[70vw]" style={{ scrollbarWidth: 'none' }}>
          {dates.map((d) => {
            const dStr = format(d, 'yyyy-MM-dd');
            const isSel = dStr === selectedDateStr;
            const isToday = dStr === format(new Date(), 'yyyy-MM-dd');
            const isDropTarget = dragOverDate === dStr;
            return (
              <button
                key={d.toISOString()}
                data-testid={`transport-new-compact-date-${dStr}`}
                onClick={() => onSelectDate(d)}
                onDragOver={(e) => { e.preventDefault(); onDragOverDate?.(dStr); }}
                onDragLeave={() => onDragLeaveDate?.(dStr)}
                onDrop={(e) => { e.preventDefault(); onDropOnDate?.(e, dStr); }}
                className={`flex flex-col items-center justify-center gap-0 px-3.5 h-12 min-w-[52px] rounded-2xl flex-shrink-0 font-bold transition-all border ${
                  isSel
                    ? 'text-white border-transparent shadow-lg scale-105'
                    : isDropTarget
                      ? 'bg-orange-200 border-orange-400 text-orange-900 ring-2 ring-orange-400'
                      : 'bg-white/10 border-transparent text-white/80 hover:bg-white/20'
                }`}
                style={isSel ? { backgroundColor: '#ee7203' } : {}}
              >
                <span className="uppercase text-[10px] opacity-80">{format(d, 'EEE')}</span>
                <span className="text-sm flex items-center gap-1">{format(d, 'd')}{isToday && !isSel && <span className="text-orange-400">•</span>}</span>
              </button>
            );
          })}
        </div>
        <button
          data-testid="transport-new-compact-next-day-btn"
          onClick={onNext}
          title="Next week"
          className="w-9 h-9 rounded-full hover:bg-white/15 flex items-center justify-center text-white flex-shrink-0"
        >
          <ChevronRight size={18} />
        </button>
        {isDateLocked && <span className="text-[10px] font-bold text-red-400 ml-1 flex-shrink-0">LOCKED</span>}
      </div>
    );
  }

  // `large`: the date strip in the Transport control bar (UI V3). The same dates, week stepping and drag-an-entry-onto-a-date as the small strip, but with big,
  // easy-to-read tiles. It is drawn taller than the bar around it, so it rises out of the bar (see .v3-datepop in v3.css).
  if (large) {
    return (
      <div className="v3-datepop flex items-center gap-2" data-testid="transport-new-compact-date-bar">
        <button
          data-testid="transport-new-compact-prev-day-btn"
          onClick={onPrev}
          title="Previous week"
          className="w-9 h-9 rounded-full hover:bg-black/10 flex items-center justify-center text-slate-700 flex-shrink-0"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="flex items-center gap-1.5 overflow-x-auto max-w-[60vw]" style={{ scrollbarWidth: 'none' }}>
          {dates.map((d) => {
            const dStr = format(d, 'yyyy-MM-dd');
            const isSel = dStr === selectedDateStr;
            const isToday = dStr === format(new Date(), 'yyyy-MM-dd');
            const isDropTarget = dragOverDate === dStr;
            return (
              <button
                key={d.toISOString()}
                data-testid={`transport-new-compact-date-${dStr}`}
                onClick={() => onSelectDate(d)}
                onDragOver={(e) => { e.preventDefault(); onDragOverDate?.(dStr); }}
                onDragLeave={() => onDragLeaveDate?.(dStr)}
                onDrop={(e) => { e.preventDefault(); onDropOnDate?.(e, dStr); }}
                className={`flex flex-col items-center justify-center gap-0.5 px-3.5 h-[50px] min-w-[62px] rounded-2xl flex-shrink-0 font-bold transition-all border ${
                  isSel
                    ? 'text-white border-transparent shadow-lg'
                    : isDropTarget
                      ? 'bg-orange-200 border-orange-400 text-orange-900 ring-2 ring-orange-400'
                      : 'bg-black/5 border-transparent text-slate-600 hover:bg-black/10'
                }`}
                style={isSel ? { backgroundColor: '#ee7203' } : {}}
              >
                <span className="uppercase text-[11px] leading-none tracking-wide opacity-90">{format(d, 'EEE')}</span>
                <span className="text-[19px] leading-none flex items-center gap-1">{format(d, 'd')}{isToday && !isSel && <span className="text-orange-500 text-base">•</span>}</span>
              </button>
            );
          })}
        </div>
        <button
          data-testid="transport-new-compact-next-day-btn"
          onClick={onNext}
          title="Next week"
          className="w-9 h-9 rounded-full hover:bg-black/10 flex items-center justify-center text-slate-700 flex-shrink-0"
        >
          <ChevronRight size={20} />
        </button>
        {isDateLocked && <span className="text-[10px] font-bold text-red-500 ml-1 flex-shrink-0">LOCKED</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1" data-testid="transport-new-compact-date-bar">
      <button
        data-testid="transport-new-compact-prev-day-btn"
        onClick={onPrev}
        title="Previous week"
        className="w-6 h-6 rounded-full hover:bg-black/10 flex items-center justify-center text-slate-700 flex-shrink-0"
      >
        <ChevronLeft size={14} />
      </button>
      <div className="flex items-center gap-1 overflow-x-auto max-w-[460px]" style={{ scrollbarWidth: 'none' }}>
        {dates.map((d) => {
          const dStr = format(d, 'yyyy-MM-dd');
          const isSel = dStr === selectedDateStr;
          const isToday = dStr === format(new Date(), 'yyyy-MM-dd');
          const isDropTarget = dragOverDate === dStr;
          return (
            <button
              key={d.toISOString()}
              data-testid={`transport-new-compact-date-${dStr}`}
              onClick={() => onSelectDate(d)}
              onDragOver={(e) => { e.preventDefault(); onDragOverDate?.(dStr); }}
              onDragLeave={() => onDragLeaveDate?.(dStr)}
              onDrop={(e) => { e.preventDefault(); onDropOnDate?.(e, dStr); }}
              className={`flex items-center gap-1 px-2 h-7 rounded-full flex-shrink-0 text-[11px] font-bold transition-all border ${
                isSel
                  ? 'text-white border-transparent shadow'
                  : isDropTarget
                    ? 'bg-orange-200 border-orange-400 text-orange-900 ring-2 ring-orange-400'
                    : 'bg-black/5 border-transparent text-slate-600 hover:bg-black/10'
              }`}
              style={isSel ? { backgroundColor: '#ee7203' } : {}}
            >
              <span className="uppercase opacity-80">{format(d, 'EEE')}</span>
              <span>{format(d, 'd')}</span>
              {isToday && !isSel && <span className="text-orange-600">•</span>}
            </button>
          );
        })}
      </div>
      <button
        data-testid="transport-new-compact-next-day-btn"
        onClick={onNext}
        title="Next week"
        className="w-6 h-6 rounded-full hover:bg-black/10 flex items-center justify-center text-slate-700 flex-shrink-0"
      >
        <ChevronRight size={14} />
      </button>
      {isDateLocked && <span className="text-[9px] font-bold text-red-500 ml-1 flex-shrink-0">LOCKED</span>}
    </div>
  );
};

export default CompactDateBar;
